import {
  BIN_ADJUST_REASONS,
  BIN_ADJUST_REASON_LABELS,
  LOCATION_TYPE_LABELS,
  movableQty,
  reservedOnShelf,
  shelfQty,
  type BinAdjustReason,
  type MapBinDetail,
  type MapBinItem,
  type MoveCheck,
  type MoveStockResult,
  type SetBinQuantityResult,
} from '@wms/domain';
import { useEffect, useRef, useState } from 'react';
import { api, ApiError, errorMessage, type FloorAction } from '../api';
import { adjustDifference, adjustProblem, moveQtyProblem, parseCount, readQtyBox, reasonFitsCount, reservedWords, staleRefusal } from '../binStock';
import { feedback } from '../feedback';
import { binCode, fmt } from '../format';
import { Modal, ProductThumb, ScanField, SellerChip, Spinner, type FloorActionRunner } from '../ui';
import { Icon } from '../ui/icons';

// The warehouse manager's (depo yöneticisi) corrections of a bin line on the map: "Taşı" and
// "Sayıyı düzelt". The API refuses them for everyone else; the map only offers them to the manager.

interface LineProps {
  readonly warehouseId: string;
  readonly bin: MapBinDetail;
  readonly item: MapBinItem;
  /** the map's runner: a press whose answer was lost is sent again with its key, also after the dialog closed */
  readonly runner: FloorActionRunner;
  readonly onClose: () => void;
  /** a refusal said the line's figures are out of date: the shelf is read again (and the dialog gets the new line) */
  readonly onStale: () => void;
}

const BUSY_TEXT = 'Önceki işlem henüz bitmedi. Bekleyip tekrar deneyin.';

/** The line a dialog works on: product, seller, lot and expiry, and the bin. */
function LineHead({ bin, item }: { bin: MapBinDetail; item: MapBinItem }) {
  return (
    <div className="stock-fix-line">
      <ProductThumb name={item.name} imageUrl={item.imageUrl} small />
      <div className="what">
        <div className="name">{item.name}</div>
        <div className="muted row" style={{ gap: 6 }}>
          <SellerChip code={item.tenantCode} />
          <span className="mono">{item.sku}</span>
          {item.lotNo && <span>Lot {item.lotNo}</span>}
          {item.expiryDate && <span className="chip">SKT {fmt.date(item.expiryDate)}</span>}
        </div>
      </div>
      <div className="stock-fix-bin">
        <span className="muted">Göz</span>
        <b className="mono">{bin.code}</b>
      </div>
    </div>
  );
}

const fillText = (ratio: number) => `%${Math.round(ratio * 100)}`;
const fillTone = (ratio: number) => (ratio < 0.5 ? 'var(--occ-low)' : ratio < 0.9 ? 'var(--occ-mid)' : 'var(--occ-high)');

/** The target bin as it is and as the move would leave it: what lies there, how full it gets. */
function TargetCard({ check }: { check: MoveCheck }) {
  const t = check.target;
  const now = t.maxVolumeCm3 ? t.usedVolumeCm3 / t.maxVolumeCm3 : null;
  const after = check.fillAfter;
  return (
    <div className="bin-box stock-fix-target">
      <div className="bin-head">
        <span className="mono">{t.code}</span>
        <span className="muted">{LOCATION_TYPE_LABELS[t.type]}</span>
        {t.isBlocked && <span className="chip bad">Kilitli</span>}
        {after !== null ? (
          <span className="fill-bar" title={`Şimdi ${now === null ? '—' : fillText(now)}, taşıyınca ${fillText(after)}`}>
            <i style={{ width: `${Math.min(100, after * 100)}%`, background: fillTone(after) }} />
          </span>
        ) : (
          now !== null && (
            <span className="fill-bar">
              <i style={{ width: `${Math.min(100, now * 100)}%`, background: fillTone(now) }} />
            </span>
          )
        )}
      </div>
      {after !== null ? (
        <div className="muted">
          Doluluk {now === null ? '—' : fillText(now)} → <b className={after > 1 ? 'over' : ''}>{fillText(after)}</b>
        </div>
      ) : (
        <div className="muted">{t.maxVolumeCm3 ? `Doluluk ${fillText(now ?? 0)}` : 'Gözün hacmi tanımlı değil: doluluk hesaplanmaz.'}</div>
      )}
      {t.items.length === 0 ? (
        <div className="muted">Boş göz</div>
      ) : (
        <ul className="bin-items">
          {t.items.map((i, n) => (
            <li key={`${i.productId}-${i.lotNo ?? ''}-${i.expiryDate ?? ''}-${n}`}>
              <div className="what">
                <div className="name">{i.name}</div>
                <div className="muted row" style={{ gap: 6 }}>
                  <SellerChip code={i.tenantCode} />
                  <span className="mono">{i.sku}</span>
                  {i.lotNo && <span>Lot {i.lotNo}</span>}
                  {i.expiryDate && <span>SKT {fmt.date(i.expiryDate)}</span>}
                </div>
              </div>
              <b className="num">{fmt.n(i.onHand)}</b>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

type CheckState =
  | { readonly state: 'loading' }
  | { readonly state: 'done'; readonly result: MoveCheck }
  | { readonly state: 'error'; readonly message: string; readonly code: string | null };

const refusalCode = (e: unknown) => (e instanceof ApiError ? e.code : null);

/**
 * "Taşı": free units of the line to another bin. The target is read with the reader or the camera,
 * or typed; before anything is booked the dialog shows what lies there, how full it gets and why the
 * move would be refused. Units promised to orders stay: the picker takes them from this bin.
 */
export function MoveDialog(props: LineProps & { readonly onMoved: (result: MoveStockResult) => void }) {
  const { warehouseId, bin, item, runner } = props;
  const movable = movableQty(item);
  const [qtyText, setQtyText] = useState(String(movable));
  const qty = parseCount(qtyText);
  const qtyProblem = moveQtyProblem(qty, item);
  const [target, setTarget] = useState<string | null>(null);
  const [check, setCheck] = useState<CheckState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const stale = useRef(props.onStale);
  stale.current = props.onStale;
  const content = useRef<HTMLDivElement>(null);
  // the count box's text after each change since it was last left, and the count it held then
  const typed = useRef<{ value: string; at: number }[]>([]);
  const typedFrom = useRef(qtyText);

  const chooseTarget = (code: string) => {
    setError(null);
    setTarget(code);
  };
  // the help says "type the count, read the target's label": a label read while the cursor is still in
  // the count box lands there, and is the target (the count typed before it stays)
  const leaveQtyBox = (value: string) => {
    const log = typed.current;
    if (log.length === 0) return;
    typed.current = [];
    const read = readQtyBox(log, value, typedFrom.current);
    setQtyText(read.qtyText);
    if (read.code !== null) chooseTarget(read.code);
  };
  const focusTarget = () => content.current?.querySelector<HTMLInputElement>('.scan input')?.focus();

  // the target as the move would leave it, once the code and the count stand still (and again when the line was read again)
  useEffect(() => {
    if (!target || qtyProblem !== null || qty === null) return setCheck(null);
    let live = true;
    setCheck({ state: 'loading' });
    const timer = setTimeout(() => {
      const query = new URLSearchParams({
        warehouseId,
        bin: bin.code,
        productId: item.productId,
        lotNo: item.lotNo ?? '',
        expiryDate: item.expiryDate ?? '',
        toBin: target,
        qty: String(qty),
      });
      api<MoveCheck>('GET', `/api/wms/bin-stock/move-check?${query}`)
        .then((result) => {
          if (!live) return;
          setCheck({ state: 'done', result });
          // the line itself was refused (emptied, reserved, counted since the panel was read): read it again
          if (result.problem?.side === 'source' && staleRefusal(result.problem.code)) stale.current();
        })
        .catch((e) => live && setCheck({ state: 'error', message: errorMessage(e), code: refusalCode(e) }));
    }, 250);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [target, qty, qtyProblem, warehouseId, bin.code, item.productId, item.lotNo, item.expiryDate, item.onHand, item.reserved, item.picked]);

  const result = check?.state === 'done' ? check.result : null;
  const capacity = result?.problem?.capacity === true;
  // another seller's stock (or another product) in the target: a warning the manager confirms with this press
  const mixing = result?.problem?.mixing === true;
  const ready = qtyProblem === null && result !== null && (result.problem === null || capacity || mixing);
  const to = result?.target.code ?? (target ? binCode(target) : null);
  const confirmWords =
    ready && qty !== null
      ? mixing
        ? `Yine de ${fmt.n(qty)} adet ${to} gözüne taşı`
        : capacity
          ? `Sığdı, yine de ${to} gözüne taşı`
          : `${fmt.n(qty)} adet ${to} gözüne taşı`
      : qtyProblem !== null
        ? 'Önce adedi düzeltin'
        : !target
          ? 'Hedef gözü okutun'
          : check?.state === 'error'
            ? check.code === 'UNKNOWN_LOCATION'
              ? 'Hedef göz bulunamadı'
              : 'Kontrol edilemedi'
            : result === null
              ? 'Hedef göz kontrol ediliyor…'
              : result.problem?.side === 'source'
                ? 'Taşınamaz'
                : 'Bu göze taşınamaz';

  const send = async (action: FloorAction) => {
    setBusy(true);
    setError(null);
    try {
      const moved = await api<MoveStockResult>(
        'POST',
        '/api/wms/bin-stock/move',
        {
          warehouseId,
          bin: bin.code,
          productId: item.productId,
          lotNo: item.lotNo,
          expiryDate: item.expiryDate,
          toBin: result!.target.code,
          qty,
          ...(capacity ? { acceptCapacity: true } : {}),
          ...(mixing ? { acceptMixing: true } : {}),
        },
        { action },
      );
      feedback('ok');
      props.onMoved(moved);
    } catch (e) {
      feedback('error');
      setError(errorMessage(e));
      if (staleRefusal(refusalCode(e))) props.onStale();
    } finally {
      setBusy(false);
    }
  };
  const confirm = () =>
    void runner.run('Taşı', send).then((sent) => {
      if (!sent) setError(BUSY_TEXT);
    });
  const close = () => {
    if (!busy) props.onClose();
  };

  return (
    <Modal
      title={`${bin.code} · taşı`}
      onClose={close}
      footer={
        <>
          <button className="btn" disabled={busy} onClick={close}>
            {movable === 0 ? 'Kapat' : 'Vazgeç'}
          </button>
          {movable > 0 && (
            <button className="btn primary" disabled={!ready || busy || runner.waiting} onClick={confirm}>
              <Icon name="move" />{' '}
              {confirmWords}
            </button>
          )}
        </>
      }
    >
      <div className="stack stock-fix" ref={content}>
        <LineHead bin={bin} item={item} />
        {runner.banner}
        {error && <div className="banner bad">{error}</div>}
        <div className="stock-fix-figures">
          <span>
            Gözde <b>{fmt.n(item.onHand)}</b>
          </span>
          {item.reserved > 0 && (
            <span>
              Siparişlere ayrılmış <b>{fmt.n(item.reserved)}</b>
            </span>
          )}
          <span>
            Taşınabilir <b>{fmt.n(movable)}</b>
          </span>
        </div>
        {item.reserved > 0 && <div className={`banner ${movable === 0 ? 'warn' : 'info-soft'}`}>{reservedWords(item, 'move')}</div>}
        {item.onHand === 0 ? (
          <p className="muted" style={{ margin: 0 }}>
            Gözde bu üründen kalmadı (sayıldı, toplandı ya da taşındı); raf yeniden okundu.
          </p>
        ) : movable === 0 ? (
          <p className="muted" style={{ margin: 0 }}>
            Bu adetler siparişleriyle birlikte gözden çıkacak; ancak bir sipariş iptal edilirse taşınabilir. Raftakiler sistemdekinden farklıysa “Sayıyı
            düzelt”i kullanın.
          </p>
        ) : (
          <>
            <div className="field stock-fix-qty">
              <label htmlFor="move-qty">Taşınacak adet</label>
              <div className="row" style={{ gap: 8, flexWrap: 'nowrap' }}>
                <input
                  id="move-qty"
                  className="input"
                  inputMode="numeric"
                  autoComplete="off"
                  maxLength={64}
                  value={qtyText}
                  // the whole count is selected, so typing replaces it (a click would put the cursor in it)
                  onFocus={(e) => e.target.select()}
                  onMouseUp={(e) => e.preventDefault()}
                  onChange={(e) => {
                    if (typed.current.length === 0) typedFrom.current = qtyText;
                    typed.current.push({ value: e.target.value, at: e.timeStamp });
                    setQtyText(e.target.value);
                  }}
                  onKeyDown={(e) => {
                    if (e.key !== 'Enter') return;
                    // the count stands (or a label read here is the target): on to the target field
                    e.preventDefault();
                    leaveQtyBox(e.currentTarget.value);
                    focusTarget();
                  }}
                  onBlur={(e) => leaveQtyBox(e.currentTarget.value)}
                />
                {qty !== movable && (
                  <button className="btn" type="button" onClick={() => setQtyText(String(movable))}>
                    Tümü ({fmt.n(movable)})
                  </button>
                )}
              </div>
              <span className={`hint${qtyProblem ? ' bad' : ''}`}>{qtyProblem ?? `En fazla ${fmt.n(movable)} adet`}</span>
            </div>
            <ScanField
              label="Hedef göz"
              placeholder="Göz etiketini okutun ya da kodu yazın"
              allowTyping
              onScan={chooseTarget}
              suggest={{ kinds: ['bin'] }}
              hint={target ? `Seçilen: ${binCode(target)} · başka bir göz okutarak değiştirin` : 'Okuyucuyla, kamerayla ya da elle; Enter ile seçin'}
            />
            {check?.state === 'loading' && <Spinner />}
            {check?.state === 'error' && <div className="banner bad">{check.message}</div>}
            {result && (
              <>
                {result.problem && (
                  <div className={`banner ${capacity || mixing ? 'warn' : 'bad'}`}>
                    {mixing
                      ? `${result.problem.message}. ${capacity ? `Kayıtlı ölçülere göre ${fmt.n(qty ?? 0)} adet ${item.sku} bu göze sığmıyor da görünüyor. ` : ''}Yine de bu göze taşıyabilirsiniz: kayda “depo yöneticisi onayladı” yazılır, her satıcının stoğu ayrı tutulur.`
                      : capacity
                        ? `${result.problem.message}. Kayıtlı ölçülere göre ${fmt.n(qty ?? 0)} adet ${item.sku} bu göze sığmıyor görünüyor; ürünler gerçekten sığıyorsa yine de taşıyabilirsiniz (kayda “depo yöneticisi onayladı” yazılır).`
                        : result.problem.message}
                  </div>
                )}
                <TargetCard check={result} />
              </>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}

/**
 * "Sayıyı düzelt": the units on the bin's shelf, as counted. Units picked into a tote are not on the
 * shelf; the ones orders still wait to pick here must stay. Booked like a count approval, with the
 * reason, and the seller's stock and marketplace stock follow. The shelf figure shown goes with the
 * count: a putaway or a pick since then is refused, and once the line is read again the count is
 * typed again (the difference it made is no longer known). Only reasons that fit the direction may be
 * chosen: damaged or lost units never add stock, found ones never take it away.
 */
export function AdjustDialog(props: LineProps & { readonly onSaved: (result: SetBinQuantityResult) => void }) {
  const { warehouseId, bin, item, runner } = props;
  const shelf = shelfQty(item);
  const least = reservedOnShelf(item);
  const [qtyText, setQtyText] = useState(String(shelf));
  const [reason, setReason] = useState<BinAdjustReason | null>(null);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const qtyBox = useRef<HTMLInputElement>(null);
  // the shelf figure the count was typed against; read again with another figure, the count is typed again
  const [countedOn, setCountedOn] = useState(shelf);
  if (countedOn !== shelf) {
    setCountedOn(shelf);
    setQtyText('');
    setError((said) => said ?? `Sistemdeki raf adedi değişti (${fmt.n(countedOn)} → ${fmt.n(shelf)}): rafı yeniden sayıp adedi tekrar yazın.`);
  }
  useEffect(() => {
    if (qtyText === '') qtyBox.current?.focus();
  }, [countedOn]);
  const newQty = parseCount(qtyText);
  const problem = adjustProblem({ newQty, reason, note }, item);
  const difference = adjustDifference(newQty, item);

  const send = async (action: FloorAction) => {
    setBusy(true);
    setError(null);
    try {
      const saved = await api<SetBinQuantityResult>(
        'POST',
        '/api/wms/bin-stock/quantity',
        {
          warehouseId,
          bin: bin.code,
          productId: item.productId,
          lotNo: item.lotNo,
          expiryDate: item.expiryDate,
          newQty,
          // what the screen said lies on the shelf: a putaway or a pick since is refused, not booked as a difference
          expectedShelf: shelf,
          reason,
          note: note.trim() || null,
        },
        { action },
      );
      feedback('ok');
      props.onSaved(saved);
    } catch (e) {
      feedback('error');
      setError(errorMessage(e));
      if (staleRefusal(refusalCode(e))) props.onStale();
    } finally {
      setBusy(false);
    }
  };
  const confirm = () =>
    void runner.run('Sayıyı düzelt', send).then((sent) => {
      if (!sent) setError(BUSY_TEXT);
    });
  const close = () => {
    if (!busy) props.onClose();
  };

  return (
    <Modal
      title={`${bin.code} · sayıyı düzelt`}
      onClose={close}
      footer={
        <>
          <button className="btn" disabled={busy} onClick={close}>
            Vazgeç
          </button>
          <button className="btn primary" disabled={problem !== null || busy || runner.waiting} onClick={confirm}>
            <Icon name="check" /> {newQty !== null && newQty !== shelf ? `Kaydet: ${fmt.n(shelf)} → ${fmt.n(newQty)}` : 'Kaydet'}
          </button>
        </>
      }
    >
      <div className="stack stock-fix">
        <LineHead bin={bin} item={item} />
        {runner.banner}
        {error && <div className="banner bad">{error}</div>}
        <div className="stock-fix-figures">
          <span>
            Sistemde rafta <b>{fmt.n(shelf)}</b>
          </span>
          {item.picked > 0 && (
            <span>
              Toplandı, sepette <b>{fmt.n(item.picked)}</b>
            </span>
          )}
          {least > 0 && (
            <span>
              Toplanmayı bekleyen <b>{fmt.n(least)}</b>
            </span>
          )}
        </div>
        {item.reserved > 0 && <div className="banner info-soft">{reservedWords(item, 'adjust')}</div>}
        <div className="field stock-fix-qty">
          <label htmlFor="adjust-qty">Rafta saydığınız adet</label>
          <input
            id="adjust-qty"
            ref={qtyBox}
            className="input"
            inputMode="numeric"
            autoComplete="off"
            autoFocus
            value={qtyText}
            onFocus={(e) => e.target.select()}
            onChange={(e) => {
              setError(null);
              setQtyText(e.target.value.replace(/\D/g, '').slice(0, 7));
            }}
          />
          <span className="hint">
            {difference ?? 'Rafta gördüğünüz adedi yazın'}
            {least > 0 && ` · en az ${fmt.n(least)}`}
          </span>
        </div>
        <div className="field">
          <label id="adjust-reason">Neden</label>
          <div className="row" role="group" aria-labelledby="adjust-reason">
            {/* a reason that says the opposite of the change is greyed out: damaged or lost never adds, found never takes off */}
            {BIN_ADJUST_REASONS.map((r) => (
              <button
                key={r}
                type="button"
                className={`chip select${reason === r ? ' on' : ''}`}
                aria-pressed={reason === r}
                disabled={!reasonFitsCount(r, newQty, item)}
                onClick={() => setReason(r)}
              >
                {BIN_ADJUST_REASON_LABELS[r]}
              </button>
            ))}
          </div>
        </div>
        <div className="field">
          <label htmlFor="adjust-note">Açıklama{reason === 'other' ? ' (zorunlu)' : ' (isteğe bağlı)'}</label>
          <input id="adjust-note" className="input" maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Ör. raf arkasında bulundu" />
        </div>
        {problem && newQty !== null && newQty !== shelf && <div className="hint bad">{problem}</div>}
        <p className="muted" style={{ margin: 0, fontSize: 13 }}>
          Fark stoğa “{reason ? BIN_ADJUST_REASON_LABELS[reason] : 'neden'}” olarak işlenir; satıcının stoğu ve stok gönderimi açık pazaryerleri birkaç dakika
          içinde güncellenir.
        </p>
      </div>
    </Modal>
  );
}
